import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import useViewport from '@hooks/useViewport';
import useWindowWidth from '@hooks/useWindowWidth';
import { cardLineStyle } from '@styles/common/Card';
import { scrollbarStyle } from '@styles/variants/scrollbar';

const mobileStyle = css`
  position: fixed;
  left: 0;
  right: 0;
  top: 50px;
  width: 100% !important;
  max-height: fit-content;
  overflow-y: auto;
`;

const DropdownWrap = styled(motion.div) <{
  direction: keyof typeof dropdownDirMap;
  mobileFilled?: boolean;
}>`
  position: absolute;
  display: flex;
  flex-direction: column;
  text-align: left;

  min-width: 150px;
  width: auto;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
  background-color: var(--cardBackgroundColor);
  padding: 8px;
  z-index: 2;
  ${cardLineStyle}
  ${scrollbarStyle}

  ${({ direction }) => direction && dropdownDirMap[direction]}

  @media (max-width: 480px) {
    ${({ mobileFilled }) => mobileFilled && mobileStyle}
  }
`;

export const topToBottomAni = {
  enter: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1],
    },
    display: 'block',
    transform: 'scaleY(1)',
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.755, 0.05, 0.855, 0.06],
    },
    transitionEnd: {
      display: 'none',
    },
    transform: 'scaleY(0.8)',
  },
};

export const dropdownDirMap = {
  topRight: css`
    top: 100%;
    right: 0;
  `,
  topLeft: css`
    top: 100%;
    left: 0;
  `,
  bottomRight: css`
    bottom: 100%;
    right: 0;
  `,
  bottomLeft: css`
    bottom: 100%;
    left: 0;
  `,
  leftTop: css`
    left: 100%;
    bottom: 0;
  `,
  rightTop: css`
    right: 100%;
    bottom: 0;
  `,
  rightBottom: css`
    right: 100%;
    top: 0;
  `,
  leftBottom: css`
    left: 100%;
    top: 0;
  `,
  bottom: css`
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
  `,
  top: css`
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
  `,
};

export const dirType = {
  TOPRIGHT: 'topRight',
  TOPLEFT: 'topLeft',
  LEFTTOP: 'leftTop',
  RIGHTTOP: 'rightTop',
  RIGHTBOTTOM: 'rightBottom',
  LEFTBOTTOM: 'leftBottom',
  BOTTOMRIGHT: 'bottomRight',
  BOTTOMLEFT: 'bottomLeft',
  BOTTOM: 'bottom',
  TOP: 'top',
};

type Props = {
  direction?: keyof typeof dropdownDirMap | undefined;
  open?: boolean;
  handleClickOutside?: (e: any, dropdownNode) => void;
  children: any;
  style?: any;
  maxHeight?: number | string;
  className?: string;
  mobileFilled?: boolean;
  mobileFull?: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
  dropdownWidth?: number;
};

function DropdownWrapper({
  direction = 'topLeft',
  open,
  handleClickOutside,
  children,
  style,
  maxHeight,
  className,
  mobileFilled,
  anchorRef,
  dropdownWidth,
  ...props
}: Props) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { viewWidth, viewHeight } = useViewport();
  const resizeWidth = useWindowWidth();
  const [fixedPos, setFixedPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  // --------------| handleClickOutside 예시 |----------------
  //
  // dropdown 바깥을 누르면 dropdown이 닫힘
  //
  // function handleClickOutside(e: any, dropdownNode) {
  //   if (targetRef.current && isUserOpen && dropdownNode) {
  //     const isClickOutside =
  //       !targetRef.current.contains(e.target) &&
  //       !dropdownNode.contains(e.target);

  //     if (isClickOutside) {
  //       setIsUserOpen(false);
  //     }
  //   }
  // }
  // --------------------------------------------------------

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', (e) => {
        if (handleClickOutside) handleClickOutside(e, dropdownRef.current);
      });
    }

    return () => {
      document.removeEventListener('mousedown', (e) => {
        if (handleClickOutside) handleClickOutside(e, dropdownRef.current);
      });
    };
  }, [open, handleClickOutside]);

  // Center to anchor for top/bottom when anchorRef provided
  useLayoutEffect(() => {
    if (!open) return;
    if (!anchorRef?.current) {
      setFixedPos(null);
      return;
    }
    if (!(direction === 'top' || direction === 'bottom')) {
      setFixedPos(null);
      return;
    }
    const compute = () => {
      const anchorEl = anchorRef.current!;
      const dropEl = dropdownRef.current;
      const aRect = anchorEl.getBoundingClientRect();
      // Temporarily ensure dropdown is measurable
      if (dropEl) {
        // Make visible for measuring if needed
        const prevDisplay = dropEl.style.display;
        if (prevDisplay === 'none') dropEl.style.display = 'block';
        const dRect = dropEl.getBoundingClientRect();
        const left = Math.round(aRect.left + aRect.width / 2 - dRect.width / 2);
        const top =
          direction === 'top'
            ? Math.round(aRect.bottom)
            : Math.round(aRect.top - dRect.height);
        setFixedPos({ left, top });
        // revert
        dropEl.style.display = prevDisplay;
      }
    };
    compute();
    const onScroll = () => compute();
    const onResize = () => compute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, anchorRef, direction, resizeWidth]);

  return (
    <DropdownWrap
      ref={dropdownRef}
      className={className}
      direction={direction}
      initial="false"
      animate="enter"
      exit="exit"
      variants={topToBottomAni}
      style={{
        transformOrigin:
          direction === dirType.TOPLEFT ||
            direction === dirType.TOPRIGHT ||
            direction === dirType.RIGHTBOTTOM ||
            direction === dirType.LEFTBOTTOM
            ? 'top'
            : 'bottom',
        // When anchored and top/bottom, use fixed positioning centered to trigger
        ...(anchorRef &&
          (direction === 'top' || direction === 'bottom') &&
          fixedPos
          ? ({
            position: 'fixed',
            left: fixedPos.left,
            top: fixedPos.top,
          } as any)
          : {}),
        // Centering by provided width (or style.width) when top/bottom without anchor
        ...(!anchorRef && (direction === 'top' || direction === 'bottom')
          ? (() => {
            const styleWidth = style?.width;
            const parsed =
              typeof dropdownWidth === 'number'
                ? dropdownWidth
                : typeof styleWidth === 'number'
                  ? styleWidth
                  : typeof styleWidth === 'string' && /\d+/.test(styleWidth)
                    ? parseInt(styleWidth, 10)
                    : undefined;
            if (parsed && Number.isFinite(parsed)) {
              return {
                left: `calc(50% - ${parsed / 2}px)`,
                width: styleWidth ? styleWidth : `${parsed}px`,
              } as any;
            }
            return {} as any;
          })()
          : {}),
        maxHeight,
        ...style,
      }}
      mobileFilled={mobileFilled}
    >
      {children}
    </DropdownWrap>
  );
}

export default DropdownWrapper;
